import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from './client'
import { keys, saveBlob } from './rooms'
import type { ChecklistResponse, InstantiateChecklistResponse } from './types'

/**
 * Request-list templates and portability (ADR 0010). Templates belong to an organization, so their
 * routes take `?organizationId=`; everything that moves a list in or out of a room is a room route.
 * The API that shipped before this has none of these routes: a caller of `useChecklistTemplates`
 * treats an error as "no stored templates" and falls back to the Master DRL compiled into the API.
 */

export interface ChecklistTemplateSummary {
  id: string
  /** Set for a system template ("master-drl"); null for an organization's own. */
  key: string | null
  name: string
  description: string | null
  dealType: string
  industryTag: string | null
  isSystem: boolean
  isOverlay: boolean
  isActive: boolean
  revision: number
  sectionCount: number
  itemCount: number
  parameterCount: number
  modifiedUtc: string | null
}

export type TemplateDataType = 'Text' | 'Date' | 'Year'

export interface ChecklistTemplateParameter {
  token: string
  label: string
  dataType: TemplateDataType
  isRequired: boolean
}

export interface ChecklistTemplateItem {
  id: string
  title: string
  requestText: string | null
  category: string | null
  priority: string | null
  requestKind: string | null
  providerNotes: string | null
}

export interface ChecklistTemplateSection {
  id: string
  code: string
  name: string
  description: string | null
  vdrIndexHint: string | null
  serviceProvider: string | null
  items: ChecklistTemplateItem[]
}

export interface ChecklistTemplate extends ChecklistTemplateSummary {
  parameters: ChecklistTemplateParameter[]
  /** Every {{Token}} the text uses, declared or not: a build asks for all of them. */
  tokens: string[]
  sections: ChecklistTemplateSection[]
}

export type MergeAction = 'add' | 'update' | 'unchanged' | 'remove' | 'skip' | 'error'

export interface ChecklistMergeRow {
  code: string
  docLabel: number | null
  title: string
  action: MergeAction
  error: string | null
  message: string | null
  sheet: string | null
  /** The row of the sheet (the record, for a CSV whose requests span lines). */
  line: number | null
  templateItemId: string | null
  alreadyThere: boolean
}

export interface ChecklistMergeResponse {
  committed: boolean
  sectionsCreated: number
  added: number
  updated: number
  unchanged: number
  removed: number
  errors: number
  rows: ChecklistMergeRow[]
  checklist: ChecklistResponse | null
}

export interface SavedChecklistTemplate {
  id: string
  name: string
  replaced: boolean
  sectionCount: number
  itemCount: number
  tokens: string[]
}

export type TemplateParameters = Record<string, string>

const data = <T>(p: Promise<{ data: T }>) => p.then((r) => r.data)

const templateKeys = {
  list: (organizationId: string) => ['checklist-templates', organizationId] as const,
  one: (organizationId: string, templateId: string) =>
    ['checklist-templates', organizationId, templateId] as const,
}

// ------------------------------------------------------------------ the organization's templates

export const useChecklistTemplates = (
  organizationId: string,
  options: { enabled?: boolean; includeInactive?: boolean } = {},
) =>
  useQuery({
    queryKey: [...templateKeys.list(organizationId), options.includeInactive ?? false] as const,
    queryFn: () =>
      data(
        api.get<ChecklistTemplateSummary[]>(
          `/api/checklist-templates?organizationId=${organizationId}` +
            (options.includeInactive ? '&includeInactive=true' : ''),
        ),
      ),
    enabled: (options.enabled ?? true) && organizationId !== '',
    retry: false,
  })

export const useChecklistTemplate = (organizationId: string, templateId: string | null) =>
  useQuery({
    queryKey: templateKeys.one(organizationId, templateId ?? ''),
    queryFn: () =>
      data(
        api.get<ChecklistTemplate>(
          `/api/checklist-templates/${templateId}?organizationId=${organizationId}`,
        ),
      ),
    enabled: templateId !== null && organizationId !== '',
    retry: false,
  })

function useTemplateInvalidator(organizationId: string) {
  const client = useQueryClient()
  return () => client.invalidateQueries({ queryKey: templateKeys.list(organizationId) })
}

export function useUpdateChecklistTemplate(organizationId: string) {
  const invalidate = useTemplateInvalidator(organizationId)
  return useMutation({
    mutationFn: ({
      templateId,
      ...body
    }: {
      templateId: string
      name: string
      description: string | null
      dealType: string
      industryTag: string | null
      isActive: boolean
      /** Null keeps the parameters as they are. */
      parameters: ChecklistTemplateParameter[] | null
    }) =>
      data(
        api.put<ChecklistTemplate>(
          `/api/checklist-templates/${templateId}?organizationId=${organizationId}`,
          body,
        ),
      ),
    onSuccess: () => void invalidate(),
  })
}

export function useCloneChecklistTemplate(organizationId: string) {
  const invalidate = useTemplateInvalidator(organizationId)
  return useMutation({
    mutationFn: ({ templateId, name }: { templateId: string; name: string }) =>
      data(
        api.post<ChecklistTemplate>(
          `/api/checklist-templates/${templateId}/clone?organizationId=${organizationId}`,
          { name },
        ),
      ),
    onSuccess: () => void invalidate(),
  })
}

export function useDeleteChecklistTemplate(organizationId: string) {
  const invalidate = useTemplateInvalidator(organizationId)
  return useMutation({
    mutationFn: (templateId: string) =>
      api.del(`/api/checklist-templates/${templateId}?organizationId=${organizationId}`),
    onSuccess: () => void invalidate(),
  })
}

// ------------------------------------------------------------------ a room's list, in and out

function useRoomInvalidator(roomId: string) {
  const client = useQueryClient()
  return () => client.invalidateQueries({ queryKey: keys.room(roomId) })
}

export function useBuildFromTemplate(roomId: string) {
  const invalidate = useRoomInvalidator(roomId)
  return useMutation({
    mutationFn: (body: {
      templateId: string
      sectionCodes: string[] | null
      parameters: TemplateParameters
      excludeItemIds?: string[]
    }) =>
      data(
        api.post<InstantiateChecklistResponse>(`/api/rooms/${roomId}/checklist/instantiate`, body),
      ),
    onSuccess: () => void invalidate(),
  })
}

/** A preview writes nothing, so only a commit invalidates the room. */
export function useChecklistOverlay(roomId: string) {
  const invalidate = useRoomInvalidator(roomId)
  return useMutation({
    mutationFn: ({
      commit,
      ...body
    }: {
      commit: boolean
      templateId: string
      parameters: TemplateParameters
      itemIds: string[] | null
    }) =>
      data(
        api.post<ChecklistMergeResponse>(
          `/api/rooms/${roomId}/checklist/overlay?mode=${commit ? 'commit' : 'preview'}`,
          body,
        ),
      ),
    onSuccess: (result) => {
      if (result.committed) void invalidate()
    },
  })
}

export function useCopyChecklistFromRoom(roomId: string) {
  const invalidate = useRoomInvalidator(roomId)
  return useMutation({
    mutationFn: (sourceRoomId: string) =>
      data(
        api.post<InstantiateChecklistResponse>(`/api/rooms/${roomId}/checklist/copy-from-room`, {
          sourceRoomId,
        }),
      ),
    onSuccess: () => void invalidate(),
  })
}

export function useSaveChecklistAsTemplate(roomId: string, organizationId: string) {
  const invalidate = useTemplateInvalidator(organizationId)
  return useMutation({
    mutationFn: (body: {
      name: string
      description: string | null
      dealType: string
      industryTag: string | null
      replaceTemplateId: string | null
    }) =>
      data(
        api.post<SavedChecklistTemplate>(`/api/rooms/${roomId}/checklist/save-as-template`, body),
      ),
    onSuccess: () => void invalidate(),
  })
}

const XLSX = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'

/** The file is the request body, as uploads are. The type is told from the name: browsers are unreliable about .csv. */
export function useImportChecklist(roomId: string) {
  const invalidate = useRoomInvalidator(roomId)
  return useMutation({
    mutationFn: ({
      file,
      commit,
      strategy,
    }: {
      file: File
      commit: boolean
      strategy: 'add' | 'replace'
    }) =>
      data(
        api.upload<ChecklistMergeResponse>(
          `/api/rooms/${roomId}/checklist/import?mode=${commit ? 'commit' : 'preview'}&strategy=${strategy}`,
          file,
          file.name.toLowerCase().endsWith('.xlsx') ? XLSX : 'text/csv',
        ),
      ),
    onSuccess: (result) => {
      if (result.committed) void invalidate()
    },
  })
}

export async function exportChecklist(roomId: string, format: 'csv' | 'xlsx'): Promise<void> {
  const { blob, fileName } = await api.download(
    `/api/rooms/${roomId}/checklist/export?format=${format}`,
  )
  saveBlob(blob, fileName ?? `request-list.${format}`)
}
